

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Input, Row, Col, Select } from "antd";
import axios from "axios";
import appUrl from "../config/appUrl";
import CustomModal from "./CustomModal";
import { showNotification } from "../utils/commonFunctions";

const { Option } = Select;

// Helper function to get role from localStorage
export const getRole = () => {
  return localStorage.getItem('role');
};

const AddNewUser = (props) => {
  const { visible, handleModal, onUserInvite } = props;
  const [state, setState] = useState({});
  const [tenantList, setTenantList] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [formError, setFormError] = useState({
    tenant_id: null,
    bot_name: null,
    email: null,
    password: null,
  });

  const userRole = getRole();
  const isAdmin = userRole === 'admin';

  // Fetch tenants for admin users
  const fetchTenants = useCallback(async () => {
    console.log('get tenants');
    if (!isAdmin) return;
    
    setLoadingTenants(true);
    try {
      const TOKEN = localStorage.getItem('token'); // or however you store your token
      const response = await axios.get(
        appUrl.TENANT,
        {
          headers: {
            Authorization: `Bearer ${TOKEN}`,
            "Content-Type": "application/json",
          }
        }
      );
      
      if (response.data.tenants) {
        setTenantList(response.data.tenants.map(tenant => ({
          value: tenant.tenant_id,
          label: tenant.organization_name,
          id: tenant.Id
        })));
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
    } finally {
      setLoadingTenants(false);
    }
  }, [isAdmin]);

  // Fetch tenants when modal opens and user is admin
  useEffect(() => {
    if (visible && isAdmin) {
      fetchTenants();
    }
  }, [visible, isAdmin, fetchTenants]);

  // Reset state when modal closes
  useEffect(() => {
    if (!visible) {
      setState({});
      setFormError({
        tenant_id: null,
        bot_name: null,
        email: null,
        password: null,
      });
    }
  }, [visible]);

  const hasError = useCallback(() => {
    let { tenant_id, bot_name, email, password } = state;
    let error = {};

    bot_name = bot_name?.trim();
    email = email?.trim();

    if (isAdmin && !tenant_id) {
      error.tenant_id = "Please select a tenant";
    }
    if (!bot_name) {
      error.bot_name = "Please enter bot name";
    }
    if (!email) {
      error.email = "Please enter email";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      error.email = "Please enter a valid email address";
    }
    if (!password) {
      error.password = "Please enter password";
    } else if (password.length < 6) {
      // error.password = "Password must be at least 6 characters";
    }

    setFormError(error);
    return !!Object.keys(error).length;
  }, [state, isAdmin]);

  const handleChange = useCallback(
    (name) => (event) => {
      let value = event?.target?.value ?? event?.target?.checked ?? event;

      setFormError({});
      setState((pre) => ({
        ...pre,
        [name]: value,
      }));
    },
    []
  );

  const handleSelectChange = useCallback(
    (name) => (value) => {
      setFormError({});
      setState((pre) => ({
        ...pre,
        [name]: value,
      }));
    },
    []
  );

  let { tenant_id, bot_name, email, password } = state;

  // const onOk = useCallback(() => {
  //   if (!hasError()) {
  //     setState((pre) => ({
  //       ...pre,
  //       loading: true,
  //     }));
      
  //     let newUser = {
  //       bot_name: bot_name?.trim(),
  //       email: email?.trim(),
  //       password: password,
  //     };

  //     // Add tenant_id to query params for admin users
  //     let queryParams = '';
  //     if (isAdmin && tenant_id) {
  //       queryParams = `?tenant_id=${tenant_id}`;
  //     }
      
  //     onUserInvite(newUser, queryParams);
  //     setState({});
  //   }
  // }, [tenant_id, bot_name, email, password, hasError, onUserInvite, isAdmin]);

  const onOk = useCallback(async () => {
  if (!hasError()) {
    setState((pre) => ({
      ...pre,
      loading: true,
    }));

    let newUser = {
      bot_name: bot_name?.trim(),
      email: email?.trim(),
      password: password,
    };

    // Add tenant_id to query params for admin users
    let queryParams = '';
    if (isAdmin && tenant_id) {
      queryParams = `?tenant_id=${tenant_id}`;
    }

    try {
      const resp = await onUserInvite(newUser, queryParams);
      console.log("User invite successful:", resp);

      // Reset form after successful submission
      setState({});
      setFormError({
        bot_name: null,
        email: null,
        password: null,
      });

      showNotification("success", "Bot created successfully!");
    } catch (err) {
      console.error("Error in onOk:", err);

      let backendErrors = {};
      let hasFieldErrors = false;

      if (err?.detail && Array.isArray(err.detail)) {
        err.detail.forEach((error) => {
          if (error.loc?.includes("bot_name")) {
            backendErrors.bot_name = error.msg;
            hasFieldErrors = true;
          }
          if (error.loc?.includes("email")) {
            backendErrors.email = error.msg;
            hasFieldErrors = true;
          }
          if (error.loc?.includes("password")) {
            backendErrors.password = error.msg;
            hasFieldErrors = true;
          }
        });
      }

      if (hasFieldErrors) {
        setFormError(backendErrors);
      } else {
        const errorMessage =
          err?.response?.data?.message ||
          err?.message ||
          err?.detail ||
          (typeof err === "string" ? err : "Failed to invite user");

        showNotification("error", errorMessage);
      }
    } finally {
      setState((pre) => ({ ...pre, loading: false }));
    }
  }
}, [tenant_id, bot_name, email, password, hasError, onUserInvite, isAdmin]);


  const renderFooter = useMemo(() => {
    return (
      <Row className="p10">
        <Button
          className="new_button"
          type="primary"
          size="large"
          onClick={onOk}
          loading={state.loading}
        >
          Add
        </Button>
        <Button
          className="new_button"
          type="primary-outline"
          onClick={handleModal}
          size="large"
        >
          Cancel
        </Button>
      </Row>
    );
  }, [handleModal, onOk, state.loading]);

  return (
    <CustomModal
      visible={visible}
      title={"Add New Bot"}
      okButtonText={"Submit"}
      onCancel={props?.handleModal}
      footer={renderFooter}
    >
      <div>
        {/* Tenant Select - Only show for admin users */}
        {isAdmin && (
          <Col className="mb10">
            <Col className="mb5">
              <label className="label">Tenant</label>
              <sup style={{ color: "red" }}>*</sup>
            </Col>
            <Col>
              <Select
                size="large"
                placeholder="Select a tenant"
                value={tenant_id}
                onChange={handleSelectChange("tenant_id")}
                style={{ width: "100%" }}
                loading={loadingTenants}
              >
                {tenantList.map((item) => (
                  <Option key={item.value} value={item.value}>
                    {item.label}
                  </Option>
                ))}
              </Select>
              <Row className="error mt5">{formError?.tenant_id}</Row>
            </Col>
          </Col>
        )}

        {/* Bot Name */}
        <Col className="mb10">
          <Col className="mb5">
            <label className="label">Bot Name</label>
            <sup style={{ color: "red" }}>*</sup>
          </Col>
          <Col>
            <Input
              size="large"
              type="text"
              value={bot_name}
              placeholder="Enter bot name"
              onChange={handleChange("bot_name")}
            />
            <Row className="error mt5">{formError?.bot_name}</Row>
          </Col>
        </Col>

        {/* Email */}
        <Col className="mb10">
          <Col className="mb5">
            <label className="label">Email</label>
            <sup style={{ color: "red" }}>*</sup>
          </Col>
          <Col>
            <Input
              size="large"
              type="email"
              value={email}
              placeholder="Enter email address"
              onChange={handleChange("email")}
            />
            <Row className="error mt5">{formError?.email}</Row>
          </Col>
        </Col>

        {/* Password */}
        <Col className="mb10">
          <Col className="mb5">
            <label className="label">Password</label>
            <sup style={{ color: "red" }}>*</sup>
          </Col>
          <Col>
            <Input
              placeholder="**********"
              type="password"
              value={password}
              onChange={handleChange("password")}
              size="large"
              allowClear
            />
            <Row className="error mt5">{formError?.password}</Row>
          </Col>
        </Col>
      </div>
    </CustomModal>
  );
};

export default AddNewUser;